import { useEffect, useRef, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { parseOAuthState } from '@/auth/authUtils';
import { useAuth } from '@/auth/useAuth';

export function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('Processing login...');
  const lastProcessedCode = useRef<string | null>(null);
  const { completeSamlLogin, handleOAuthCallback } = useAuth();

  useEffect(() => {
    const code = searchParams.get('code');
    const hasSession = searchParams.get('session') === '1';

    if (code && lastProcessedCode.current !== code) {
      lastProcessedCode.current = code;
      handleCallback();
    } else if (hasSession && lastProcessedCode.current !== '__saml__') {
      lastProcessedCode.current = '__saml__';
      handleCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state') ?? '';
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const hasSession = searchParams.get('session') === '1';

      if (errorParam) {
        throw new Error(errorDescription || `SSO Error: ${errorParam}`);
      }

      if (hasSession) {
        await completeSamlLogin();
      } else if (!code) {
        throw new Error('No authorization code received from SSO provider');
      } else {
        await handleOAuthCallback(code, state);
      }

      setStatus('Login successful! Redirecting...');

      const stateData = parseOAuthState(state);
      const returnUrl = hasSession ? '/' : stateData.return_url || '/';
      window.location.href = returnUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed. Please try again.';
      setError(message);

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6 py-12">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-200">
        {error ? (
          <>
            <h1 className="text-xl font-semibold text-gray-900">Authentication failed</h1>
            <p className="mt-3 text-sm text-red-600">{error}</p>
            <p className="mt-6 text-sm text-gray-600">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-gray-200 border-t-gray-900" />
            <h1 className="mt-6 text-lg font-semibold text-gray-900">{status}</h1>
            <p className="mt-2 text-sm text-gray-600">Please wait while we complete your login.</p>
          </>
        )}
      </div>
    </div>
  );
}
